import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "./constants";
import { statusUi, type Option, type UploadSlot, type UploadedDoc } from "./helpers";

const DF_CSS = `
.df-page .content { padding-top: 8px; }
.df-head {
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.df-sub {
  margin-top: 10px;
  color: #1f2430;
  font-weight: 700;
  font-size: 16px;
}
.df-card, .df-request, .df-note {
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.df-card { padding: 16px 18px 6px; margin-bottom: 16px; }
.df-note { padding: 14px 16px; margin-bottom: 16px; }
.df-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2430;
  font-size: 15px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1f6;
}
.df-card-title::before {
  content: "";
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: ${BRAND};
  flex: 0 0 auto;
}
.df-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 20px;
}
.df-fields > * { grid-column: 1 / -1; }
.df-fields > .df-field { grid-column: auto; min-width: 0; }
.df-fields > .df-field.df-span { grid-column: 1 / -1; }
.df-request { margin-bottom: 14px; overflow: hidden; }
.df-request-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #fafbfc;
  border-bottom: 1px solid #eef1f6;
}
.df-ref {
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.02em;
}
.df-request-body { padding: 14px 16px 6px; }
.df-kv { padding-bottom: 12px; }
.df-k { color: #6b7280; font-size: 12px; font-weight: 600; }
.df-v { color: #111827; font-weight: 600; margin-top: 2px; word-break: break-word; }
.df-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  bottom: 12px;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 12px;
  padding: 12px 16px;
  margin: 8px 0 16px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
}
.df-input, .df-page .form-control, .df-page .form-select {
  background: #fff;
  border: 1px solid #d7dee9;
  border-radius: 8px;
  min-height: 42px;
  color: #111827;
}
.df-page .form-control:focus, .df-page .form-select:focus {
  border-color: #b9c3d4;
  box-shadow: 0 0 0 3px rgba(192, 40, 44, 0.08);
}
.df-page .form-control:disabled, .df-page .form-control[readonly] {
  background: #f4f6f9;
  color: #6b7280;
}
.df-pick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}
@media (max-width: 767px) {
  .df-fields { grid-template-columns: 1fr; }
  .df-fields > .df-field { grid-column: 1 / -1; }
  .df-actions { flex-direction: column; }
}
`;

export function PageShell({
  title,
  subtitle,
  children,
  onSync,
  syncing,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onSync?: () => void;
  syncing?: boolean;
}) {
  return (
    <div className="page-wrapper df-page" style={{ background: "#f3f5f9", minHeight: "100vh" }}>
      <style>{DF_CSS}</style>
      <div className="content" style={{ paddingBottom: 28 }}>
        <div className="df-head">
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div>
              <h3 className="mb-1" style={{ color: "#111827", fontWeight: 700, fontSize: 22 }}>
                {title}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">{title}</li>
                </ol>
              </nav>
            </div>
            {onSync && (
              <button
                type="button"
                className="btn btn-sm"
                style={{ border: `1px solid ${BRAND}`, color: BRAND, background: "#fff", borderRadius: 8, fontWeight: 600 }}
                onClick={onSync}
                disabled={syncing}
              >
                <i className="ti ti-refresh me-1" />
                {syncing ? "Syncing..." : "Sync"}
              </button>
            )}
          </div>
          {subtitle && <div className="df-sub">{subtitle}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status?: string }) {
  const ui = statusUi(status);
  if (!ui) return <span>-</span>;
  return (
    <span
      style={{
        background: ui.color,
        color: "#fff",
        borderRadius: 4,
        padding: "4px 10px",
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {ui.label}
    </span>
  );
}

export function RequestCard({
  reference,
  lines,
  onView,
}: {
  reference: string;
  lines: React.ReactNode;
  onView?: () => void;
}) {
  return (
    <div className="df-request">
      <div className="df-request-head">
        <span className="df-ref">{reference || "-"}</span>
        {onView && (
          <button
            type="button"
            onClick={onView}
            className="btn btn-sm"
            style={{ color: BRAND, border: `1px solid ${BRAND}`, background: "#fff", borderRadius: 8, fontWeight: 600 }}
          >
            <i className="ti ti-eye me-1" /> View Details
          </button>
        )}
      </div>
      <div className="df-fields df-request-body">{lines}</div>
    </div>
  );
}

export function Line({ label, value, wide }: { label: string; value?: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`df-field df-kv${wide ? " df-span" : ""}`}>
      <div className="df-k">{label}</div>
      <div className="df-v">{value || "-"}</div>
    </div>
  );
}

export function EmptyState({ text = "No records found" }: { text?: string }) {
  return (
    <div className="text-center py-5">
      <h5 className="fw-bold" style={{ color: "#000" }}>
        {text}
      </h5>
    </div>
  );
}

export function CreateBar({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="d-flex justify-content-end mb-3">
      <button
        type="button"
        className="btn text-white d-inline-flex align-items-center"
        style={{ background: BRAND, borderRadius: 8, padding: "10px 16px", fontWeight: 600 }}
        onClick={onClick}
      >
        <i className="ti ti-circle-plus me-2" /> {label}
      </button>
    </div>
  );
}

export function InstructionModal({
  open,
  text,
  onOk,
  onClose,
}: {
  open: boolean;
  text: string;
  onOk: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="bg-white"
        style={{ width: "min(640px, 100%)", borderRadius: 10, padding: 20, maxHeight: "85vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h5 className="fw-bold" style={{ color: "#000" }}>
          Instructions
        </h5>
        <div style={{ overflow: "auto", color: "#000", fontSize: 14, lineHeight: "20px", whiteSpace: "pre-wrap" }}>
          {text.replace(/\\n/g, "\n")}
        </div>
        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="btn text-white px-4" style={{ background: BRAND, borderRadius: 8 }} onClick={onOk}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export function Section({ title, required, children }: { title?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="df-card">
      {title && (
        <div className="df-card-title">
          {title}
          {required && <span style={{ color: "#FF0F0F" }}> *</span>}
        </div>
      )}
      <div className="df-fields">{children}</div>
    </div>
  );
}

export function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  const only = React.Children.toArray(children)[0] as React.ReactElement | undefined;
  const wide = only?.type === TextArea;
  return (
    <div className={`mb-3 df-field${wide ? " df-span" : ""}`}>
      <label className="form-label mb-1" style={{ color: "#5B6270", fontWeight: 600 }}>
        {label}
        {required && <span style={{ color: "#FF0F0F" }}> *</span>}
      </label>
      {children}
      {error && <div style={{ color: "#FF0F0F", fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const inputStyle = (disabled?: boolean): React.CSSProperties => ({
  background: disabled ? "#f4f6f9" : "#fff",
  color: disabled ? "#6b7280" : "#111827",
  border: "1px solid #d7dee9",
  borderRadius: 8,
  minHeight: 42,
});

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement> & { disabled?: boolean }) {
  return <input {...props} className="form-control" style={inputStyle(props.disabled || props.readOnly)} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className="form-control" rows={props.rows || 3} style={inputStyle(false)} />;
}

export function SelectInput({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  placeholder: string;
}) {
  return (
    <select className="form-select" style={inputStyle(false)} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={`${o.id}-${o.label}`} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function DateField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const iso = /^\d{2}\.\d{2}\.\d{4}$/.test(value)
    ? `${value.slice(6, 10)}-${value.slice(3, 5)}-${value.slice(0, 2)}`
    : "";
  return (
    <div style={{ position: "relative" }}>
      <input
        className="form-control"
        placeholder="DD.MM.YYYY"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle(false), paddingRight: 42 }}
      />
      <input
        type="date"
        aria-label="DD.MM.YYYY"
        value={iso}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return onChange("");
          const [y, m, d] = v.split("-");
          onChange(`${d}.${m}.${y}`);
        }}
        style={{ position: "absolute", right: 8, top: 8, width: 28, height: 26, opacity: 0, cursor: "pointer" }}
      />
      <i className="ti ti-calendar" style={{ position: "absolute", right: 12, top: 12, color: "#6b7280", pointerEvents: "none" }} />
    </div>
  );
}

export function RadioGroup({
  value,
  options,
  onChange,
  column,
}: {
  value: string | null;
  options: string[];
  onChange: (v: string) => void;
  column?: boolean;
}) {
  return (
    <div className={column ? "d-flex flex-column gap-2" : "d-flex flex-wrap gap-2"}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className="btn text-start"
            style={{
              background: active ? `${BRAND}12` : "#F3F5FA",
              border: `1px solid ${active ? BRAND : "#E2E8F0"}`,
              color: "#2B2E38",
              borderRadius: 8,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: `2px solid ${BRAND}`,
                marginRight: 8,
                verticalAlign: "middle",
                position: "relative",
              }}
            >
              {active && (
                <span
                  style={{
                    position: "absolute",
                    inset: 2,
                    borderRadius: "50%",
                    background: BRAND,
                  }}
                />
              )}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function UploadSlotButton({
  label,
  required,
  slot,
  onPick,
  onClear,
  hint,
}: {
  label: string;
  required?: boolean;
  slot: UploadSlot;
  onPick: (file: File) => void;
  onClear: () => void;
  hint?: string;
}) {
  return (
    <div className="mb-3 df-field df-span">
      <div className="mb-1" style={{ color: "#5B6270", fontWeight: 600, fontSize: 14 }}>
        {label}
        {required && <span style={{ color: "#FF0F0F" }}> *</span>}
      </div>
      <label
        className="d-flex align-items-center"
        style={{ border: "1px dashed #C9D2E3", background: "#F8FAFF", borderRadius: 8, padding: 12, cursor: "pointer" }}
      >
        <i className="ti ti-cloud-upload me-2" style={{ color: BRAND }} />
        <span style={{ color: BRAND, fontWeight: 600 }}>{slot.isUploading ? "Uploading..." : slot.fileName || "Choose File"}</span>
        <input
          type="file"
          hidden
          disabled={slot.isUploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onPick(f);
          }}
        />
      </label>
      {hint && <div style={{ color: "#8A93A8", fontSize: 12, marginTop: 4 }}>{hint}</div>}
      {slot.key && (
        <button type="button" className="btn btn-link btn-sm text-danger p-0 mt-1" onClick={onClear}>
          Delete
        </button>
      )}
    </div>
  );
}

export function DocUploader({
  title,
  hint,
  docs,
  onAdd,
  onRemove,
  required,
}: {
  title: string;
  hint?: string;
  docs: UploadedDoc[];
  onAdd: (file: File) => void;
  onRemove: (index: number) => void;
  required?: boolean;
}) {
  return (
    <Section title={title} required={required}>
      <label
        className="d-flex align-items-center"
        style={{ border: "1px dashed #C9D2E3", background: "#F8FAFF", borderRadius: 8, padding: 12, cursor: "pointer" }}
      >
        <i className="ti ti-cloud-upload me-2" style={{ color: BRAND, fontSize: 20 }} />
        <div>
          <div style={{ color: BRAND, fontWeight: 600 }}>Choose File</div>
          {hint && <div style={{ color: "#8A93A8", fontSize: 12 }}>{hint}</div>}
        </div>
        <input
          type="file"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onAdd(f);
          }}
        />
      </label>
      {docs.length > 0 && (
        <div className="mt-3">
          {docs.map((d, i) => (
            <div key={`${d.key}-${i}`} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: "1px solid #ECEFF5" }}>
              <span style={{ color: "#111" }}>{d.fileName}</span>
              <button type="button" className="btn btn-link text-danger p-0" onClick={() => onRemove(i)}>
                <i className="ti ti-trash" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export function ActionBar({
  left,
  right,
  onLeft,
  onRight,
  busy,
}: {
  left: string;
  right: string;
  onLeft: () => void;
  onRight: () => void;
  busy?: boolean;
}) {
  return (
    <div className="df-actions">
      <button type="button" className="btn btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onLeft} disabled={busy}>
        {left}
      </button>
      <button type="button" className="btn text-white px-4" style={{ background: BRAND, borderRadius: 8 }} onClick={onRight} disabled={busy}>
        {busy ? "Submitting..." : right}
      </button>
    </div>
  );
}

export function InfoRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="row py-2" style={{ borderBottom: "1px solid #F1F3F7" }}>
      <div className="col-md-5" style={{ color: "#5B6270", fontWeight: 600 }}>
        {label}
      </div>
      <div className="col-md-7" style={{ color: "#111", whiteSpace: "pre-wrap" }}>
        {value === undefined || value === null || value === "" ? "-" : value}
      </div>
    </div>
  );
}

export function SubtitleBanner({ text }: { text: string }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-3" style={{ background: "#EEF2FF", borderLeft: `4px solid ${BRAND}`, padding: "12px 14px", borderRadius: 6 }}>
      <i className="ti ti-info-circle" style={{ color: "#3B4A7A" }} />
      <span style={{ color: "#3B4A7A", fontWeight: 600 }}>{text}</span>
    </div>
  );
}

export function NoteBox({ title, text }: { title: string; text: string }) {
  return (
    <div className="mb-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: 12 }}>
      <div className="fw-bold mb-1" style={{ color: "#92400E" }}>
        <i className="ti ti-alert-triangle me-1" /> {title}
      </div>
      <div style={{ color: "#78350F" }}>{text}</div>
    </div>
  );
}

export function AlertModal({
  open,
  title,
  message,
  onOk,
}: {
  open: boolean;
  title: string;
  message: string;
  onOk: () => void;
}) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="bg-white" style={{ width: "min(480px, 100%)", borderRadius: 10, padding: 20 }}>
        <h5 className="fw-bold" style={{ color: "#000" }}>{title}</h5>
        <p style={{ color: "#000" }}>{message}</p>
        <button type="button" className="btn text-white w-100" style={{ background: BRAND }} onClick={onOk}>
          OK
        </button>
      </div>
    </div>
  );
}

export function ConfirmModal({
  open,
  title,
  message,
  onYes,
  onNo,
}: {
  open: boolean;
  title: string;
  message: string;
  onYes: () => void;
  onNo: () => void;
}) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="bg-white" style={{ width: "min(420px, 100%)", borderRadius: 10, padding: 20 }}>
        <h5 className="fw-bold">{title}</h5>
        <p style={{ whiteSpace: "pre-line" }}>{message}</p>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary flex-fill" onClick={onNo}>
            No
          </button>
          <button type="button" className="btn text-white flex-fill" style={{ background: BRAND }} onClick={onYes}>
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}
